import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import CategoryDropdown from "./CategoryDropdown";
import { MAIN_SITE_URL, ARTICLE_SLUG, categories } from "@/constants/site";

export default function Navbar() {
  const { t } = useTranslation();
  const { lang = "en" } = useParams();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-40 border-b border-background-200 bg-background-50 transition-shadow duration-300 ${
        scrolled ? "shadow-sm" : ""
      }`}
    >
      <nav className="mx-auto flex h-16 w-full items-center justify-between gap-4 px-4 md:px-6">
        <Link
          to={`/${lang}`}
          className="flex items-center gap-2 text-foreground-950"
          onClick={() => setMobileOpen(false)}
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-grid-line text-base leading-none" />
          </span>
          <span className="whitespace-nowrap text-base font-bold tracking-tight">
            {t("brand.name")}
          </span>
        </Link>

        <div className="hidden items-center gap-1 md:flex">
          <Link
            to={`/${lang}`}
            className="rounded-md px-3 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
          >
            <span className="whitespace-nowrap">{t("nav.home")}</span>
          </Link>
          <Link
            to={`/${lang}#guides`}
            className="rounded-md px-3 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
          >
            <span className="whitespace-nowrap">{t("nav.shoppingGuide")}</span>
          </Link>
          <CategoryDropdown />
        </div>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 sm:inline-flex"
          >
            {t("nav.cta")}
          </a>
          <button
            type="button"
            onClick={() => setMobileOpen((o) => !o)}
            className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-800 transition-colors hover:bg-background-100 md:hidden"
            aria-label="Toggle menu"
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl leading-none`} />
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="max-h-[75vh] overflow-y-auto border-t border-background-200 bg-background-50 px-4 py-4 md:hidden">
          <div className="flex flex-col gap-1">
            <Link
              to={`/${lang}`}
              className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-home-line mr-2" />
              {t("nav.home")}
            </Link>
            <Link
              to={`/${lang}#guides`}
              className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-compass-3-line mr-2" />
              {t("nav.shoppingGuide")}
            </Link>
            <Link
              to={`/${lang}/article/${ARTICLE_SLUG}`}
              className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-file-text-line mr-2" />
              {t("nav.guides")}
            </Link>
          </div>

          <p className="mt-5 px-3 text-xs font-semibold uppercase tracking-wide text-foreground-500">
            {t("categories.title")}
          </p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {categories.map((c) => (
              <a
                key={c.key}
                href={c.url}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
              >
                <span className="relative h-10 w-12 shrink-0 overflow-hidden">
                  <img
                    src={c.image}
                    alt={t(`categories.${c.key}`)}
                    className="absolute inset-0 h-full w-full object-cover object-top"
                  />
                </span>
                <span className="whitespace-nowrap pr-2 text-xs font-medium text-foreground-800">
                  {t(`categories.${c.key}`)}
                </span>
              </a>
            ))}
          </div>

          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="mt-5 block whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-center text-sm font-semibold text-background-50"
          >
            {t("nav.cta")}
          </a>
        </div>
      )}
    </header>
  );
}